import React, { useState, useEffect, useCallback } from 'react';
import { KpiCard, ModuleHeader, toneColor } from '@/design-system/dna';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageSquare, Bell, Megaphone, Target, FileText, Mail,
  Send, Users, ChevronRight, RefreshCw, Clock, Sparkles,
  ArrowRight, Zap, AlertCircle, CheckCircle2, Star,
  Radio, Activity, Eye
} from 'lucide-react';
import { cn } from '../../../lib/utils';

const API_URL = window.location.origin || '';
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } };
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] } } };

// ── Mission Status ──────────────────────────
// ── KPI Card ────────────────────────────────
const KPICard = ({ title, value, icon, color, sub, onClick }) => (
  <KpiCard id={`com-${String(title).toLowerCase().replace(/\s+/g, '-')}`} icon={icon} label={title} value={value} hint={sub} color={toneColor(color)} onClick={onClick} />
);

// ── Channel Card ────────────────────────────
const ChannelCard = ({ title, icon: Icon, color, items, onClick }) => {
  const p = {
    cyan: { bg: '#F0FDFF', border: '#CFFAFE', accent: '#0891B2' },
    purple: { bg: '#FAF5FF', border: '#EDE9FE', accent: '#7B3F98' },
    amber: { bg: '#FFFBEB', border: '#FEF3C7', accent: '#D97706' },
    green: { bg: '#F0FDF9', border: '#D1FAE5', accent: '#059669' },
    rose: { bg: '#FFF5F6', border: '#FFE4E6', accent: '#E11D48' },
    blue: { bg: '#F0F5FF', border: '#DBEAFE', accent: '#2563EB' },
  }[color] || {};
  return (
    <motion.div variants={fadeUp} onClick={onClick}
      className="rounded-2xl p-5 cursor-pointer hover:-translate-y-[1px] hover:shadow-md transition-all border"
      style={{ background: p.bg, borderColor: p.border }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg" style={{ background: `${p.accent}10` }}>
            <Icon className="h-4 w-4" style={{ color: p.accent }} />
          </div>
          <span className="text-[14px] font-semibold text-slate-700">{title}</span>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </div>
      <div className="space-y-2.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between">
            <span className="text-[12px] text-slate-500">{it.label}</span>
            <span className="text-[14px] font-bold tabular-nums" style={{ color: it.value > 0 ? p.accent : '#94A3B8' }}>{it.value}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// ── Quick Action ────────────────────────────
const QuickAction = ({ icon: Icon, label, color, onClick }) => (
  <motion.button variants={fadeUp} onClick={onClick}
    data-testid={`comm-action-${label?.toLowerCase().replace(/\s/g, '-')}`}
    className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-white border border-slate-100 hover:shadow-md hover:-translate-y-[1px] transition-all"
    whileHover={{ boxShadow: `0 4px 20px ${color}15` }}>
    <div className="p-1.5 rounded-lg" style={{ background: `${color}12` }}>
      <Icon className="h-4 w-4" style={{ color }} />
    </div>
    <span className="text-[13px] font-medium text-slate-600">{label}</span>
  </motion.button>
);

// ═══════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════
export default function Comunicacao() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const h = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/v2/comunicacao/resumo`, { headers: h });
      setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="space-y-5 animate-pulse" data-testid="comm-loading">
        <div className="h-12 rounded-2xl bg-slate-100" />
        <div className="grid grid-cols-6 gap-3">{[...Array(6)].map((_, i) => <div key={i} className="h-28 rounded-2xl bg-slate-100" />)}</div>
        <div className="grid grid-cols-3 gap-4">{[...Array(3)].map((_, i) => <div key={i} className="h-44 rounded-2xl bg-slate-100" />)}</div>
      </div>
    );
  }

  const d = data || {};
  const msgs = d.mensagens_total || 0;
  const notifs = d.notificacoes_total || 0;
  const unread = d.notificacoes_nao_lidas || 0;
  const campanhas = d.campanhas_total || 0;
  const campanhasAtivas = d.campanhas_ativas || 0;
  const templates = d.templates_total || 0;
  const comunicados = d.comunicados_total || 0;
  const alertas = d.alertas_total || 0;

  // Insights
  const insights = [];
  if (unread > 0) insights.push({ text: `${unread} notificações não lidas pelas famílias.`, action: 'Ver notificações', color: '#D97706', path: '/v2/comunicacao/mensagens' });
  if (campanhasAtivas > 0) insights.push({ text: `${campanhasAtivas} campanha(s) ativa(s) em andamento.`, action: 'Ver campanhas', color: '#7B3F98', path: '/v2/comunicacao/campanhas' });
  if (alertas > 0) insights.push({ text: `${alertas} alerta(s) registrado(s) no sistema.`, action: 'Ver alertas', color: '#E11D48', path: '/v2/comunicacao/alertas' });
  if (insights.length === 0) insights.push({ text: 'Comunicação operando normalmente. Sem pendências.', color: '#10B981' });

  return (
    <motion.div className="space-y-5" initial="hidden" animate="visible" variants={stagger} data-testid="comunicacao-hub">

      {/* 1. MISSION STATUS */}
      <motion.div variants={fadeUp}>
        <ModuleHeader kicker="Comunicação" title="Comunicação" icon={MessageSquare} testid="comunicacao-header"
          subtitle="Mensagens, notificações, campanhas, mural e alertas das famílias"
          actions={<Button onClick={load} className="dna-mh-primary" data-testid="comunicacao-refresh"><RefreshCw className="h-4 w-4" /> Atualizar</Button>} />
      </motion.div>

      {/* 2. KPIs */}
      <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" variants={stagger}>
        <KPICard title="Mensagens" value={msgs} icon={MessageSquare} color="cyan" onClick={() => navigate('/v2/comunicacao/mensagens')} />
        <KPICard title="Notificações" value={notifs} icon={Bell} color="purple" sub={unread > 0 ? `${unread} não lidas` : 'Todas lidas'} onClick={() => navigate('/v2/comunicacao/mensagens')} />
        <KPICard title="Campanhas" value={campanhas} icon={Target} color="amber" sub={campanhasAtivas > 0 ? `${campanhasAtivas} ativas` : 'Nenhuma ativa'} onClick={() => navigate('/v2/comunicacao/campanhas')} />
        <KPICard title="Comunicados" value={comunicados} icon={Megaphone} color="blue" onClick={() => navigate('/v2/comunicacao/mural')} />
        <KPICard title="Templates" value={templates} icon={FileText} color="green" onClick={() => navigate('/v2/comunicacao/templates')} />
        <KPICard title="Alertas" value={alertas} icon={AlertCircle} color={alertas > 0 ? 'rose' : 'green'} onClick={() => navigate('/v2/comunicacao/alertas')} />
      </motion.div>

      {/* 3. CHANNELS + INSIGHTS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Channels — 2 cols */}
        <motion.div className="lg:col-span-2" variants={stagger}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Canais de Comunicação</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ChannelCard title="Mensagens" icon={MessageSquare} color="cyan"
              items={[
                { label: 'Total enviadas', value: msgs },
                { label: 'Não lidas', value: unread },
                { label: 'Lidas', value: notifs - unread },
              ]} onClick={() => navigate('/v2/comunicacao/mensagens')} />
            <ChannelCard title="Campanhas" icon={Target} color="purple"
              items={[
                { label: 'Total', value: campanhas },
                { label: 'Ativas', value: campanhasAtivas },
                { label: 'Finalizadas', value: campanhas - campanhasAtivas },
              ]} onClick={() => navigate('/v2/comunicacao/campanhas')} />
            <ChannelCard title="Mural" icon={Megaphone} color="amber"
              items={[
                { label: 'Comunicados', value: comunicados },
                { label: 'Templates', value: templates },
              ]} onClick={() => navigate('/v2/comunicacao/mural')} />
            <ChannelCard title="DNA Connect" icon={Users} color="green"
              items={[
                { label: 'Notificações famílias', value: notifs },
                { label: 'Não lidas', value: unread },
              ]} onClick={() => navigate('/v2/familia/admin')} />
          </div>
        </motion.div>

        {/* Insights */}
        <motion.div variants={fadeUp}>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-3.5 w-3.5 text-violet-500" />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">IA Comunicação</span>
          </div>
          <div className="rounded-2xl p-4 space-y-3 bg-white border border-slate-100 shadow-sm">
            {insights.map((ins, i) => (
              <button key={i} onClick={() => ins.path && navigate(ins.path)}
                className="w-full p-3.5 rounded-xl text-left border-l-[3px] hover:translate-x-0.5 transition-all"
                style={{ borderLeftColor: ins.color, background: `${ins.color}06` }}>
                <p className="text-[12px] text-slate-600 leading-relaxed">{ins.text}</p>
                {ins.action && <span className="text-[11px] font-semibold mt-1.5 flex items-center gap-1" style={{ color: ins.color }}>{ins.action} <ArrowRight className="h-3 w-3" /></span>}
              </button>
            ))}
          </div>

          {/* Recent messages */}
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Atividade Recente</span>
            </div>
            <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm">
              {(d.mensagens_recentes || []).length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-[12px] text-slate-400">Nenhuma atividade recente</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {(d.mensagens_recentes || []).slice(0, 5).map((m, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                      <Mail className="h-3.5 w-3.5 text-cyan-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] text-slate-700 truncate">{m.subject || m.title || 'Mensagem'}</p>
                        <p className="text-[10px] text-slate-400">{m.created_at ? new Date(m.created_at).toLocaleDateString('pt-BR') : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* 4. QUICK ACTIONS */}
      <motion.div variants={fadeUp}>
        <div className="flex items-center gap-2 mb-3">
          <Zap className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Ações Rápidas</span>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <QuickAction icon={Send} label="Enviar Mensagem" color="#0891B2" onClick={() => navigate('/v2/comunicacao/mensagens')} />
          <QuickAction icon={Megaphone} label="Novo Comunicado" color="#D97706" onClick={() => navigate('/v2/comunicacao/mural')} />
          <QuickAction icon={Target} label="Nova Campanha" color="#7B3F98" onClick={() => navigate('/v2/comunicacao/campanhas')} />
          <QuickAction icon={FileText} label="Templates" color="#059669" onClick={() => navigate('/v2/comunicacao/templates')} />
          <QuickAction icon={Star} label="Aniversariantes" color="#E11D48" onClick={() => navigate('/v2/comunicacao/aniversariantes')} />
          <QuickAction icon={Users} label="DNA Connect" color="#2563EB" onClick={() => navigate('/v2/familia/admin')} />
        </div>
      </motion.div>
    </motion.div>
  );
}
